import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { Link } from 'react-router-dom';
import { Media, Nav } from 'react-bootstrap';

import { AuthContext } from 'components/providers/AuthContextProvider';
import { GDSN_DATA_SOURCE_ID, NAVIGATION_SIDE_BAR_WIDTH } from 'lib/constants';
import { backgroundGrey3, blueviolet, white } from 'lib/css/colors';
import {
  fsaClipboardCheck,
  fsaDashboard,
  fsaDataSources,
  fsaDataStreams,
  fsaEnrichProducts,
  fsaJobs,
  fsaManageAttributes,
  fsaManageTaxonomy,
  fsaProducts,
  fsaUPB,
  fsaSettings,
  fsaSignOut,
  fsaStats,
  fsaReports,
} from 'images/icons/navbar';
import userPic from 'lib/img/avatar.jpg';
import Logo from 'lib/img/fsaLogo.png';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import SideNavItem from 'components/layout/sidenav/SideNavItem';
import SideNavItemWithChildren from 'components/layout/sidenav/SideNavItemWithChildren';
import UserPermission from 'lib/enums/UserPermission';

function AppSideNavBar({ expanded }) {
  const { authUser, homeUrl, logout } = useContext(AuthContext);
  const hasUserPermissions = useUserPermissions();

  return (
    <div
      className={classNames(
        css(styles.container, expanded && styles.containerExpanded),
        'd-flex flex-column',
        !expanded && 'd-none',
      )}
    >
      <div className="mt-5">
        <Nav className={css(styles.nav)}>
          <Nav.Link
            className={classNames(css(styles.link, styles.name), 'text-white font-weight-bold')}
          >
            <Media>
              <img
                className={css(styles.circle)}
                src={authUser?.picture_url || userPic}
                alt="User Avatar"
              />
            </Media>
          </Nav.Link>
          {hasUserPermissions(
            [
              UserPermission.VIEW_DASHBOARD,
              UserPermission.UPB_VIEW_ADMIN,
              UserPermission.ENRICH_PRODUCTS,
            ],
            false,
          ) ? (
            <SideNavItemWithChildren
              title="Dashboard"
              icon={fsaDashboard}
              submenuItems={[
                {
                  url: '/dashboard',
                  title: 'Dashboard',
                  permissions: [UserPermission.VIEW_DASHBOARD],
                },
                {
                  url: '/upb-match-explorer',
                  title: 'UPB Match Explorer',
                  permissions: [UserPermission.UPB_VIEW_ADMIN],
                },
                {
                  url: '/influence-sku-mapping',
                  title: 'Influence SKU Mapping',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions(
            [UserPermission.CLASSIFY, UserPermission.MANAGE_PRODUCT_LISTS],
            false,
          ) ? (
            <SideNavItemWithChildren
              title="Products"
              icon={fsaProducts}
              submenuItems={[
                {
                  url: '/all-products',
                  title: 'All Products',
                  permissions: [UserPermission.CLASSIFY],
                },
                {
                  url: '/product-lists',
                  title: 'Product Lists',
                  permissions: [UserPermission.MANAGE_PRODUCT_LISTS],
                },
                {
                  url: '/global-product-linkages',
                  title: 'Manage Global Product Links',
                  permissions: [UserPermission.VIEW_SKU_MAPPINGS],
                },
                {
                  url: '/vendor-products-links-suggestions',
                  title: 'Products Links Suggestions',
                  permissions: [UserPermission.VIEW_SKU_MAPPINGS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.UPB_VIEW, UserPermission.UPB_SALES_VIEW], false) ? (
            <SideNavItemWithChildren
              title="UPB"
              icon={fsaUPB}
              submenuItems={[
                {
                  url: '/upb-viewer',
                  title: 'UPB Viewer',
                  permissions: [UserPermission.UPB_VIEW],
                },
                {
                  url: '/upb-sales-viewer',
                  title: 'UPB Sales Viewer',
                  permissions: [UserPermission.UPB_SALES_VIEW],
                },
                {
                  url: '/upb-viewer-beta',
                  title: 'UPB Viewer Beta',
                  permissions: [UserPermission.UPB_VIEW],
                },
                {
                  url: '/upb-mfr-viewer',
                  title: 'UPB Mfr Viewer',
                  permissions: [UserPermission.UPB_VIEW],
                },
                {
                  url: '/upb-distributor-viewer',
                  title: 'UPB Distributor Viewer',
                  permissions: [UserPermission.UPB_VIEW],
                },
                {
                  url: '/upb-mfr-viewer-beta',
                  title: 'UPB Mfr Viewer Beta',
                  permissions: [UserPermission.UPB_VIEW],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.EDIT_ATTRIBUTES]) ? (
            <SideNavItemWithChildren
              title="Attributes"
              icon={fsaManageAttributes}
              submenuItems={[
                {
                  url: '/assign-attributes',
                  title: 'Assign Attributes',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
                {
                  url: '/manage-attributes',
                  title: 'Manage Attributes',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
                {
                  url: '/manage-schema',
                  title: 'Manage Schema',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
                {
                  url: '/manage-attribute-value-alternatives',
                  title: 'Manage Attribute Value Alternatives',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
                {
                  url: '/manage-display-values',
                  title: 'Manage Display Values',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
                {
                  url: '/attribute-scoring',
                  title: 'Attribute Scoring',
                  permissions: [UserPermission.EDIT_ATTRIBUTES],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.CLASSIFY, UserPermission.MANAGE_TAXONOMY], false) ? (
            <SideNavItemWithChildren
              title="Classify"
              icon={fsaManageTaxonomy}
              submenuItems={[
                {
                  url: '/global-products/classify',
                  title: 'Classify',
                  permissions: [UserPermission.CLASSIFY],
                },
                {
                  url: '/manage-taxonomy',
                  title: 'Manage Taxonomy',
                  permissions: [UserPermission.MANAGE_TAXONOMY],
                },
                {
                  url: '/l4-segmentation',
                  title: 'L4 Segmentation',
                  permissions: [UserPermission.MANAGE_TAXONOMY],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.MANAGE_DATA_SOURCES]) ? (
            <SideNavItemWithChildren
              title="Sources"
              icon={fsaDataSources}
              submenuItems={[
                {
                  url: '/data-sources',
                  title: 'Data Sources',
                  permissions: [UserPermission.MANAGE_DATA_SOURCES],
                },
                {
                  url: '/data-sources-dashboard',
                  title: 'Data Sources Dashboard',
                  permissions: [UserPermission.MANAGE_DATA_SOURCES],
                },
                {
                  url: `/data-sources/${GDSN_DATA_SOURCE_ID}`,
                  title: 'GDSN',
                  permissions: [UserPermission.MANAGE_DATA_SOURCES],
                },
                {
                  url: '/product-content-validator',
                  title: 'Scraped Product Validator',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/product-enrichment-resources',
                  title: 'Product Enrichment Resources',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/nutritional-panel-upload',
                  title: 'Upload Nutritional Panels',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/cut-dry-import-tracking',
                  title: 'Cut Dry Import Tracking',
                  permissions: [UserPermission.MANAGE_DATA_SOURCES],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.MANAGE_DATA_STREAMS]) ? (
            <SideNavItemWithChildren
              title="Streams"
              icon={fsaDataStreams}
              submenuItems={[
                {
                  url: '/data-streams',
                  title: 'Data Streams',
                  permissions: [UserPermission.MANAGE_ASSETS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.VIEW_JOBS]) ? (
            <SideNavItemWithChildren
              title="Jobs"
              icon={fsaJobs}
              submenuItems={[
                {
                  url: '/jobs',
                  title: 'Jobs',
                  permissions: [UserPermission.VIEW_JOBS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions(
            [UserPermission.ENRICH_PRODUCTS, UserPermission.BRAND_HARVESTING],
            false, // hasAll
          ) ? (
            <SideNavItemWithChildren
              title="Enrich"
              icon={fsaEnrichProducts}
              submenuItems={[
                {
                  url: '', // No direct URL for this parent item
                  title: 'Brands',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                  children: [
                    {
                      url: '/enrich-products/manage-brands',
                      title: 'Manage Brands',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/brand-verify',
                      title: 'Brand Verify',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/brand-qa',
                      title: 'QA Brands',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/brand-harvest',
                      title: 'Brand Harvesting Tool',
                      permissions: [UserPermission.BRAND_HARVESTING],
                    },
                    {
                      url: '/enrich-products/supplier-brand-management',
                      title: 'Supplier Brand Management',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/brand-cleanup',
                      title: 'Brand Cleanup',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/aws-brand-sync',
                      title: 'AWS Brand Sync',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                  ],
                },
                {
                  url: '', // No direct URL for this parent item
                  title: 'Images',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                  children: [
                    {
                      url: '/enrich-products/image-suggester',
                      title: 'Image Suggester',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/image-tagger2',
                      title: 'Image Tagger 2',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/l4-default-images',
                      title: 'L4 Default Images',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                  ],
                },
                {
                  url: '',
                  title: 'Manage Manufacturers',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                  children: [
                    {
                      url: '/enrich-products/manage-manufacturers',
                      title: 'Manage Manufacturers',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                    {
                      url: '/enrich-products/global-manufacturer-harvest',
                      title: 'Global Manufacturer Harvest',
                      permissions: [UserPermission.ENRICH_PRODUCTS],
                    },
                  ],
                },
                {
                  url: '/enrich-products/manage-vendors',
                  title: 'Manage Distributors',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/gs1-lookup',
                  title: 'GS1 Look Up',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/sku-mapping-workflow',
                  title: 'SKU Mapping Workflow',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/brand-sku-mapper',
                  title: 'Brand SKU Mapper',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/brand-sku-mapper-v2',
                  title: 'Brand SKU Mapper V2',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/brand-sku-mapper-mfr-to-dp',
                  title: 'Brand SKU Mapper MFR → DP',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/products-with-a-sale-in-1m-and-unmapped',
                  title: 'Products with a sale in 1M and unmapped',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
                {
                  url: '/enrich-products/products-with-a-sale-in-1m-and-unstructured',
                  title: 'Products with a sale in 1M and unstructured',
                  permissions: [UserPermission.ENRICH_PRODUCTS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([UserPermission.VIEW_DASHBOARD]) ? (
            <SideNavItem
              title="Support"
              url="/support-tickets"
              icon={fsaClipboardCheck}
              count={true}
            />
          ) : null}
          {hasUserPermissions(
            [
              UserPermission.ADMIN_OPERATIONS,
              UserPermission.VIEW_FILE_EXPORTS,
              UserPermission.VIEW_DATA_REPORTS,
            ],
            false,
          ) ? (
            <SideNavItemWithChildren
              title="Reports"
              icon={fsaReports}
              submenuItems={[
                {
                  url: '/file-exports',
                  title: 'File Exports',
                  // Data Reports users need this page to download the generated files.
                  permissions: [UserPermission.VIEW_FILE_EXPORTS, UserPermission.VIEW_DATA_REPORTS],
                  anyPermission: true,
                },
                {
                  url: '/generate-reports/activity',
                  title: 'Activity Reports',
                  permissions: [UserPermission.ADMIN_OPERATIONS],
                },
                {
                  url: '/time-logs/admin',
                  title: 'Time Reports',
                  permissions: [UserPermission.ADMIN_OPERATIONS],
                },
                {
                  url: '/rebate-analysis',
                  title: 'Rebate Analysis',
                  permissions: [UserPermission.ADMIN_OPERATIONS],
                },
                {
                  url: '/data-reports',
                  title: 'Data Reports',
                  permissions: [UserPermission.VIEW_DATA_REPORTS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions(
            [UserPermission.VIEW_USER_MANAGEMENT, UserPermission.DATA_SYNC],
            false,
          ) ? (
            <SideNavItemWithChildren
              title="Settings"
              icon={fsaSettings}
              submenuItems={[
                {
                  url: '/settings/team',
                  title: 'Team',
                  permissions: [UserPermission.VIEW_USER_MANAGEMENT],
                },
                {
                  url: '/sync-data',
                  title: 'Sync Data',
                  permissions: [UserPermission.DATA_SYNC],
                },
                {
                  url: '/task-view',
                  title: 'Task View',
                  permissions: [UserPermission.TASKS_VIEW],
                },
                {
                  url: '/settings/activity-logs',
                  title: 'Activity Logs',
                  permissions: [UserPermission.MANAGE_ACTIVITY_LOGS],
                },
              ]}
            />
          ) : null}
          {hasUserPermissions([[UserPermission.VIEW_SKU_MAPPINGS]]) ? (
            <SideNavItem title="Stats" url="/stats" icon={fsaStats} />
          ) : null}
          <Link to={homeUrl}>
            <div
              className={classNames(
                'd-flex align-items-center',
                css(styles.bottomLogo),
                'justify-content-center',
              )}
            >
              <img src={Logo} alt="fsaLogo" className={css(styles.logo)} />
            </div>
          </Link>
          <SideNavItem title="Sign Out" url="" onClick={() => logout} icon={fsaSignOut} />
          <Link to={homeUrl}>
            <div
              className={classNames(
                'd-flex align-items-center',
                css(styles.bottomLogo),
                'justify-content-center',
              )}
            >
              <img src={Logo} alt="fsaLogo" className={css(styles.logo)} />
            </div>
          </Link>
        </Nav>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  bottomLogo: {
    position: 'fixed',
    bottom: '10px',
    width: NAVIGATION_SIDE_BAR_WIDTH - 4,
  },
  container: {
    backgroundColor: white,
    height: '100%',
    left: '-100vw',
    maxWidth: NAVIGATION_SIDE_BAR_WIDTH,
    position: 'fixed',
    top: 0,
    width: '80vw',
    zIndex: 1500, // high enough to be over the fade.
    borderRight: '4px solid ' + backgroundGrey3,
  },
  containerExpanded: {
    left: 0,
    color: white,
  },
  circle: {
    borderRadius: '60%',
    width: '100%',
    backgroundColor: blueviolet,
    objectFit: 'contain',
  },
  link: {
    padding: '0.5rem',
  },
  logo: {
    width: '90%',
  },
  name: {
    marginBottom: 10,
  },
  nav: {
    backgroundColor: white,
    flexDirection: 'column',
    maxWidth: NAVIGATION_SIDE_BAR_WIDTH,
    minHeight: '100%',
    minWidth: NAVIGATION_SIDE_BAR_WIDTH,
    animation: 'margin-left .75s forwards',
    position: 'fixed',
    borderRight: '4px solid ' + backgroundGrey3,
  },
});

AppSideNavBar.propTypes = {
  expanded: PropTypes.bool,
};

export default AppSideNavBar;
